import type { StatusTone } from "../../../shared/components";
import type { Lang } from "../../../shared/i18n";

/**
 * F3's copy.
 *
 * 🔴 A SEPARATE DICTIONARY FROM `PLATFORM_COPY`, AND THE REASON IS A TEST.
 * `copy.test.ts` asserts the platform dictionary contains no `mrr`, `revenue` or
 * `invoice` in either language — that assertion survived OD-117's reopening and
 * is the half of the old tripwire that still matters. Keeping the subscription
 * vocabulary out of that object means the guard keeps guarding exactly what it
 * was written to guard, rather than being widened until it means nothing.
 *
 * ⚠️ NOTHING HERE NAMES A PRICE. Every figure on the screen comes from a plan an
 * operator created; this file supplies only the words around it.
 */
export const BILLING_COPY = {
  en: {
    title: "Subscriptions",
    subtitle:
      "Which school is on which plan, until when, and what happened to it.",

    /* 🔴 The rule travels with the number, so the figure cannot be quoted
       without what produced it (OD-86). */
    seatsRule:
      "Seats are counted from live enrolment — every child in a class that is still running. Nobody self-reports a seat count.",
    everySchool: "Every school",
    everySchoolHint:
      "Schools on no plan are listed too — that is usually the row worth opening.",
    needsAttention: (n: number) =>
      n === 1
        ? "1 school is behind on payment"
        : `${n} schools are behind on payment`,
    dunningLadder:
      "Past due, then a grace period. Suspending a school is a separate act on its lifecycle, and it never deletes anything.",

    school: "School",
    plan: "Plan",
    noPlan: "No plan",
    seats: "Seats",
    charge: "Per period",
    renews: "Renews",
    status: "Status",
    open: "Open",
    onPlanCount: (on: number, total: number) =>
      `${on} of ${total} schools are on a plan`,

    /* States. */
    stateTrial: "Trial",
    stateActive: "Active",
    statePastDue: "Past due",
    stateGrace: "In grace",
    stateCancelled: "Cancelled",
    stateNone: "No subscription",

    /* One school. */
    /* The dialog's accessible name. A screen reader announces the panel before
       the school name inside it, so it says what KIND of thing opened. */
    schoolDialog: "School subscription",
    close: "Close",
    changePlan: "Plan",
    pickPlan: "Choose a plan",
    perStudent: "child",
    startPlan: "Start this plan",
    movePlan: "Move to this plan",
    suspendElsewhere: (schoolStatus: string) =>
      `This school's access is ${schoolStatus.toLowerCase()}. Suspending or restoring access is done on the school itself, not here — and it never deletes anything.`,

    recordPayment: "Record what happened",
    /* 🔴 STILL SAYS PLAINLY THAT NOTHING IS BEING CHARGED — and now it has to
       work harder, because a payment provider IS connected. This form is the
       transfer-and-cheque route; the school's own screen is the card route.
       An operator who confuses the two records money that never arrived. */
    recordHint:
      "This records what you were told; it does not take a payment. Money paid by card or UPI arrives on its own and appears in Payments below.",
    paidUntil: "Paid up to",
    markPaid: "Record a payment",
    markPastDue: "Mark past due",
    markGrace: "Move to grace",
    cancel: "Cancel the subscription",
    note: "Note",
    noteHint: 'A reference somebody will need in six months — "NEFT ref 8821".',

    timeline: "What has happened",
    noTimeline: "Nothing recorded yet.",
    when: "When",
    what: "What",

    /*
     * ── paying ──────────────────────────────────────────────────────────────
     *
     * 🔴 THE WORDS ARE CAREFUL BECAUSE THE ACTION IS IRREVERSIBLE AND THE
     * CONFIRMATION IS NOT INSTANT. Payment is confirmed by a webhook, so a
     * school that returns from Razorpay may see a screen that has not caught up
     * — and a school that does not know that will pay a second time. Every
     * string below is written to prevent that one mistake.
     */
    payNow: "Pay now",
    payOpening: "Opening Razorpay…",
    payFor: (amount: string, period: string) =>
      `${amount} for the next ${period}.`,
    /** 🔴 Said BEFORE they pay, not after they come back. */
    payConfirmHint:
      "You will be taken to Razorpay. Once you have paid, this page updates within a minute — you do not need to pay again if it still says “past due”.",
    payFailed: "That could not be started. Try again in a moment.",
    /** ⚠️ A sentence naming the other way to pay, never a disabled button. */
    payUnavailable:
      "Online payment is not switched on for this school. Your Kalqy contact will send an invoice, and payment by transfer is recorded here once it arrives.",

    paymentsTitle: "Payments",
    paymentsHint:
      "Every payment, across every school. A card or UPI row was confirmed by the bank; a recorded row is what a colleague was told.",
    noPayments: "No payments yet.",
    amount: "Amount",
    /** The rail — "upi", "card". ⚠️ Never an instrument. */
    how: "How",
    paymentStatus: "State",
    source: "Route",
    online: "Card or UPI",
    byHand: "Recorded by Kalqy",
    paidState: "Paid",
    pendingState: "Not paid yet",
    failedState: "Failed",
    refundedState: "Refunded",

    /* The three periods a plan can run on. */
    periodMonthly: "month",
    periodQuarterly: "quarter",
    periodYearly: "year",

    /* The catalogue. */
    catalogue: "Plans",
    catalogueHint:
      "You set the rates. Nothing in the product ships a price, and changing one applies to future periods only.",
    /* The trigger that opens the form; `createPlan` is the submit inside it. */
    newPlan: "Create a plan",
    createPlan: "Create the plan",
    creating: "Creating…",
    planCode: "Code",
    planCodeHint:
      "Lowercase and hyphens. It cannot be changed — the audit trail refers to it.",
    planName: "Name",
    perStudentPrice: "Per child",
    floorPrice: "Least per school",
    amountHint: "Digits, up to two decimal places.",
    floorHint: "What the smallest school pays, whatever its seat count.",
    visibility: "Visibility",
    live: "Live",
    retired: "Retired",
    published: "On the price list",
    unlisted: "Not listed",
    publish: "Put on the price list",
    unlist: "Take off the list",
    retire: "Retire",
    restore: "Bring back",
    noPlans: "No plans yet. “Create a plan” sets the first rate.",
    planRejected: "That plan was not created. The code may already be in use.",
    publishOnCreate: "Show this plan on the public price list",
    /* 🔴 The consequence, because the failure is silent: nothing warns you, the
       plan simply is not there. */
    publishHint:
      "Leave this off for a rate agreed with one school. While it is off, the plan works normally but no school can see it on the Plans page.",
    unlistedCount: (n: number) =>
      n === 1
        ? "One plan is live but not on the public price list"
        : `${String(n)} plans are live but not on the public price list`,
    unlistedDetail:
      "That is correct for a rate agreed with one school. If it was meant to be public, use “Put on the price list” — until then the Plans page shows visitors that pricing is not published yet.",
    billingPeriod: "How often",
    periodHint: "The rate above is per child per this.",
    /* ⚠️ THE SAME THREE WORDS THE FORM'S DROPDOWN OFFERS. The list and the form
       naming a plan's period differently — "month" against "Monthly" — is how a
       reader ends up checking whether they are the same thing. */
    periodWord: { MONTHLY: "month", QUARTERLY: "quarter", YEARLY: "year" },
    planBlurb: "One line for the price list",
    blurbHint:
      "Only shown for a published plan. Leave it empty for a bespoke rate.",
    /* 🔴 Deliberately says nothing was changed. A half-applied move is the fear
       an operator has when a button goes quiet, and the answer is "no". */
    /* ── change 19 §16 — the coupon form ─────────────────────────────────── */
    /* 🔴 "per child" IS IN THE LABEL, NOT ONLY IN THE HINT. The two flat kinds
       differ by a factor of the school's whole enrolment, and a label that says
       only "Amount off" beside a figure of 10 is the sentence that produces a
       ₹10 discount where ₹900 was agreed. */
    /* ── change 19 §3 · a school's own money, on its own screen ─────────── */
    spendTitle: "What this has cost",
    spendHint:
      "Taken from payments that have actually been received. A payment still in progress is not counted here.",
    spendPaidEver: "Paid, all time",
    spendTwelveMonths: "Paid in the last 12 months",
    spendPerChild: "Per child, this period",
    /* 🔴 The seat count beside the rate, because it is what produced it. */
    spendPerChildHint: (seats: number) =>
      `Across ${String(seats)} ${seats === 1 ? "child" : "children"} enrolled today.`,
    spendPerChildNone: "No children enrolled yet.",
    spendDiscount: (amount: string) =>
      `Your discount takes ${amount} off this period at today's enrolment.`,
    spendNothingYet: "No payments have been received yet.",
    spendChartLabel: "What this school paid, month by month, over the last year",

    kindPerStudent: "An amount off per child",
    perChildSuffix: " /child",
    perStudentOff: "Off per child (₹)",
    perStudentOffHint:
      "Multiplied by the school’s live enrolment. ₹10 a head off ninety children is ₹900 off that period’s charge.",
    expiresInDays: "Expires in (days)",
    expiresNever: "Leave this empty and the code never stops working.",
    expiresOn: (date: string) => `The code stops working on ${date}.`,
    schoolsLoadFailed:
      "Couldn’t load the school list. The coupon can still be created for any school.",
    couponCreated: (code: string) => `${code} is live and can be typed by a school.`,
    actionFailed:
      "That did not go through. Nothing was changed — check the connection and try again.",
    editPlan: "Edit",
    closeEdit: "Close",
    savePlan: "Save the plan",
    saving: "Saving…",
    /* 🔴 The consequence above the button — G1's rule. A rate change that
       silently re-billed last month is the fear this sentence answers. */
    editMeaning:
      "This changes what schools on this plan pay from their next period. Payments already made are not touched, and the code stays as it is.",

    /*
     * ── trial rows on the subscriptions table ───────────────────────────────
     *
     * 🔴 THE WORDS SAY "ENDS ON", NEVER "FOR 30 DAYS". A trial with no end is
     * how a free customer nobody decided to give away comes into existence, and
     * these are the words that name that state on the school's own row.
     *
     * ⚠️ WHAT IS LEFT HERE IS READ-ONLY. The free-trials screen and its grant
     * form are gone; a `TRIAL` row still arrives from the server, so it still
     * has to be nameable, but nothing in the dashboard starts or extends one.
     */
    trialEnds: "Trial ends",
    trialEndsOn: (date: string) => `Free until ${date}`,
    /* ⚠️ Named as the state it is, so a row cannot be misread as a paying one. */
    trialNoEnd: "Free trial with no end date",

    /*
     * ── the discounts screen ────────────────────────────────────────────────
     */
    couponsTitle: "Discount codes",
    couponsSubtitle:
      "Write a code, say what it takes off and how long it lasts, then check what it comes to before you offer it.",
    /** 🔴 The calculator's three figures, in the order they are read. */
    calcTitle: "What it comes to",
    calcHint:
      "Nothing here is saved. It reads a real plan's rate and works out what a school of this size would pay — the same arithmetic the server does when the code is actually claimed.",
    calcPlan: "Plan",
    calcSeats: "Children",
    calcList: "List price",
    calcOff: "Discount",
    calcNet: "They pay",
    calcPerStudent: (rate: string) => `${rate} per child`,
    /**
     * ⚠️ THE FLOOR IS PART OF THE ANSWER AND IS EASY TO FORGET. A plan has a
     * minimum charge; a small school's list price is the floor rather than
     * seats × rate, and a discount comes off the figure that actually applies.
     */
    calcFloorApplied: (floor: string) => `The plan's minimum of ${floor} applies at this size.`,
    calcNoPlan: "Choose a plan to see the figures.",
    calcNothingOff: "This code takes nothing off at this size.",
    couponStartsAt: "Live from",
    couponStartsAtHint: "Leave it empty and it works from now.",
    couponWindow: "Window",
    couponWindowOpen: "No end date",
    couponNotYetLive: "Not live yet",

    /*
     * ── discounts ───────────────────────────────────────────────────────────
     *
     * 🔴 EVERY STRING HERE DISTINGUISHES THE RULE FROM THE SAVING, because the
     * two look alike on a screen and mean different things. "20%" is the coupon;
     * "₹400" is what one school saves at today's enrolment, and it moves when a
     * child joins.
     */
    discount: "Discount",
    noDiscount: "None",
    listPrice: "List price",
    afterDiscount: "They pay",
    discountOff: (off: string, code: string) => `${off} off · ${code}`,
    couponPeriodsLeft: (n: number) =>
      n === 1
        ? "Last discounted period"
        : `${String(n)} discounted periods left`,
    couponForever: "For as long as it is attached",

    applyCoupon: "Apply a discount code",
    applyCouponHint:
      "The code names a discount you created. What it comes to is worked out here from live enrolment — nothing on this form sets an amount.",
    couponCode: "Code",
    applyIt: "Apply",
    applying: "Applying…",
    removeCoupon: "Remove the discount",
    removeCouponHint:
      "The claim stays on record — removing it does not give the code back to somebody else. The next charge is the full plan price.",
    couponRejected: "That code was not applied.",

    coupons: "Discounts",
    couponsHint:
      "You define these. Nothing in the product ships a discount, and what a school actually saves is worked out from live enrolment every time it is shown.",
    createCoupon: "Create the discount",
    couponCodeHint:
      "What a school types. Uppercased, and it cannot be changed afterwards — it will be sitting in somebody’s inbox.",
    couponName: "Name",
    couponNameHint: 'For you, not for them — "Kerala pilot, first term".',
    couponKind: "Kind",
    kindPercent: "A percentage",
    kindAmount: "A flat amount",
    percentOff: "Percent off",
    percentHint: "A whole number, 1 to 100.",
    amountOff: "Amount off",
    amountOffHint:
      "Digits, up to two decimal places. In the plan’s own currency.",
    duration: "How many periods",
    durationHint:
      "Leave empty for as long as it stays attached. Counted in periods paid, never in months.",
    maxRedemptions: "How many schools",
    maxRedemptionsHint:
      "Leave empty for no limit. A school re-applying its own code does not use a second one.",
    boundSchool: "Only this school",
    boundSchoolHint:
      "Paste a school id for a rate agreed with one school. A limit of one is not the same control — that is a race, won by whoever types fastest.",
    newSchoolsOnly: "Only schools that have never paid",
    newSchoolsOnlyHint:
      "The joining offer. “New” means they have never paid, not that they signed up recently — a school whose paperwork took five weeks still qualifies.",
    startsAt: "Live from",
    expiresAt: "Live until",
    claims: "Claims",
    claimsOf: (used: number, max: number | null) =>
      max === null ? `${String(used)}` : `${String(used)} of ${String(max)}`,
    /* ⚠️ Read twice: as a table cell ("this coupon is not bound") and as the
       picker's default option ("do not bind it"). One sentence covers both. */
    anySchool: "Any school that types the code",
    noCoupons: "No discounts yet.",
    couponRejectedOnCreate:
      "That discount was not created. The code may already be in use, or the form needs exactly one of a percentage and an amount.",
    retireCoupon: "Retire",
    restoreCoupon: "Bring back",
    retiredCouponDetail:
      "Schools already on it keep the discount. What stops is new claims.",

    redemptions: "Discounts claimed",
    redemptionsHint:
      "What each school was given, and what it came to on the day. These figures are a snapshot — the live saving moves with the roster.",
    noRedemptions: "Nothing claimed yet.",
    onTheDay: "On the day",
    saved: "Came off",

    /* States shared with the surface. */
    loadFailed: "Couldn’t load this.",
    writeFailed: "That did not save.",
    noSchools: "No schools on the platform yet.",
    lastWorked: (at: string) => `Last loaded ${at}`,
    neverWorked: "It hasn’t loaded yet",
    retry: "Try again",
  },
} as const;

/**
 * A payment state → words.
 *
 * ⚠️ "Not paid yet" RATHER THAN "Pending", because the two audiences read the
 * same row: an operator wants the state and a school wants to know whether they
 * owe anything. `PENDING` here means a link was raised and nobody has paid it —
 * which for a school is "not paid yet" and for an operator is the same fact.
 */
export function paymentStatusLabel(status: string, lang: Lang): string {
  const t = BILLING_COPY[lang];
  switch (status) {
    case "PAID":
      return t.paidState;
    case "FAILED":
      return t.failedState;
    case "REFUNDED":
      return t.refundedState;
    default:
      return t.pendingState;
  }
}

/**
 * A billing period → the noun that follows "for the next".
 *
 * 🔴 IT IS EXHAUSTIVE OVER THE ENUM AND FALLS BACK TO THE MONTH. A fourth period
 * added to the schema would render as "month" here rather than as the enum
 * name — which is the failure this function is FOR, since a screen showing
 * `QUARTERLY` to a school is worse than one showing a slightly wrong word. The
 * type-level guarantee is `BillingPeriod` itself, which `types:check` keeps in
 * step with the schema.
 */
export function periodLabel(period: string, lang: Lang): string {
  const t = BILLING_COPY[lang];
  switch (period) {
    case "QUARTERLY":
      return t.periodQuarterly;
    case "YEARLY":
      return t.periodYearly;
    default:
      return t.periodMonthly;
  }
}

/** A status enum → the operator's language. */
/**
 * A subscription status → a pill tone.
 *
 * 🔴 ONE MAP, BECAUSE THE SCREEN HAD THREE COPIES OF THE SAME LADDER and each
 * one had to be read to find out whether GRACE was amber or grey. A tone is a
 * property of the status, not of the table it happens to be rendered in.
 */
export function statusTone(status: string): StatusTone {
  if (status === "ACTIVE") return "success";
  if (status === "PAST_DUE" || status === "GRACE") return "warning";
  if (status === "CANCELLED") return "danger";
  return "neutral";
}

export function statusLabel(status: string, lang: Lang): string {
  const t = BILLING_COPY[lang];
  switch (status) {
    case "TRIAL":
      return t.stateTrial;
    case "ACTIVE":
      return t.stateActive;
    case "PAST_DUE":
      return t.statePastDue;
    case "GRACE":
      return t.stateGrace;
    case "CANCELLED":
      return t.stateCancelled;
    default:
      return t.stateNone;
  }
}

/**
 * A timeline verb → words.
 *
 * ⚠️ IT FALLS BACK TO THE RAW ENUM RATHER THAN TO A BLANK. A value added to
 * `SubscriptionAction` and not to this map should read as an ugly constant an
 * operator can search for, not as an empty cell that hides a transition.
 */
export function actionLabel(action: string): string {
  const en: Record<string, string> = {
    STARTED: "Subscription started",
    PLAN_CHANGED: "Moved plan",
    PAYMENT_RECORDED: "Payment recorded",
    MARKED_PAST_DUE: "Marked past due",
    ENTERED_GRACE: "Moved to grace",
    RESUMED: "Resumed",
    CANCELLED: "Cancelled",
    /* 🔴 GRANTED vs CHANGED. "We gave this school six weeks" and "we gave them
       six more" are different facts, and one label for both loses the one that
       gets asked about. */
    TRIAL_GRANTED: "Free trial started",
    TRIAL_CHANGED: "Trial end date changed",
    COUPON_APPLIED: "Discount applied",
    COUPON_REMOVED: "Discount removed",
  };
  return en[action] ?? action;
}
